
import React from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown } from 'lucide-react';
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { LeaderboardData } from './types';

interface DataTableProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  leaderboardData: LeaderboardData[];
  title: string;
}

const DataTable: React.FC<DataTableProps> = ({
  isOpen,
  setIsOpen,
  leaderboardData,
  title
}) => {
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="w-full border border-primary/20 rounded-lg overflow-hidden">
      <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-secondary/30 hover:bg-secondary/50 transition-colors">
        <h3 className="text-primary-light font-medium ml-2">{title}</h3>
        <div className="flex items-center">
          <span className="text-primary text-xs mr-2">(Click to expand or collapse)</span>
          <ChevronDown className={`h-5 w-5 text-primary transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="p-4 bg-secondary/10">
          <div className="overflow-x-auto">
            <Table className="w-full text-[10px]">
              <TableCaption>Pipeline stages data for selected filters</TableCaption>
              <TableHeader>
                <TableRow className="border-b border-primary/20 bg-secondary/50">
                  <TableHead className="h-10 px-2 text-left align-middle font-medium text-primary text-[10px]">SN</TableHead>
                  <TableHead className="h-10 px-2 text-left align-middle font-medium text-primary text-[10px]">Account Name</TableHead>
                  <TableHead className="h-10 px-2 text-left align-middle font-medium text-primary text-[10px]">Name</TableHead>
                  <TableHead className="h-10 px-2 text-left align-middle font-medium text-primary text-[10px]">Region</TableHead>
                  <TableHead className="h-10 px-2 text-left align-middle font-medium text-primary text-[10px]">Country</TableHead>
                  <TableHead className="h-10 px-2 text-right align-middle font-medium text-primary text-[10px]">Target ($)</TableHead>
                  <TableHead className="h-10 px-2 text-right align-middle font-medium text-primary text-[10px]">Achievement ($)</TableHead>
                  <TableHead className="h-10 px-2 text-right align-middle font-medium text-primary text-[10px]">Prospecting ($)</TableHead>
                  <TableHead className="h-10 px-2 text-right align-middle font-medium text-primary text-[10px]">Qualification ($)</TableHead>
                  <TableHead className="h-10 px-2 text-right align-middle font-medium text-primary text-[10px]">Proposal ($)</TableHead>
                  <TableHead className="h-10 px-2 text-right align-middle font-medium text-primary text-[10px]">Negotiation ($)</TableHead>
                  <TableHead className="h-10 px-2 text-right align-middle font-medium text-primary text-[10px]">LateStage to Gap</TableHead>
                  <TableHead className="h-10 px-2 text-right align-middle font-medium text-primary text-[10px]">HistWin Ratio (%)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leaderboardData.length > 0 ? (
                  leaderboardData.map((item, index) => {
                    return (
                      <TableRow key={index} className="border-b border-white/10 hover:bg-secondary/30">
                        <TableCell className="px-2 py-2 text-left text-[10px]">{index + 1}</TableCell>
                        <TableCell className="px-2 py-2 text-left text-[10px]">{item.account_name || "N/A"}</TableCell>
                        <TableCell className="px-2 py-2 text-left text-[10px]">{item.name || "N/A"}</TableCell>
                        <TableCell className="px-2 py-2 text-left text-[10px]">{item.region}</TableCell>
                        <TableCell className="px-2 py-2 text-left text-[10px]">{item.country}</TableCell>
                        <TableCell className="px-2 py-2 text-right text-[10px]">${item.target.toLocaleString()}</TableCell>
                        <TableCell className="px-2 py-2 text-right text-[10px]">${item.achievement.toLocaleString()}</TableCell>
                        <TableCell className="px-2 py-2 text-right text-[10px]">${(item.prospecting_stage_value || 0).toLocaleString()}</TableCell>
                        <TableCell className="px-2 py-2 text-right text-[10px]">${(item.qualification_stage_value || 0).toLocaleString()}</TableCell>
                        <TableCell className="px-2 py-2 text-right text-[10px]">${(item.proposal_stage_value || 0).toLocaleString()}</TableCell>
                        <TableCell className="px-2 py-2 text-right text-[10px]">${(item.negotiation_stage_value || 0).toLocaleString()}</TableCell>
                        <TableCell className="px-2 py-2 text-right text-[10px]">
                          {item.late_stage_to_gap_ratio !== null ? `${item.late_stage_to_gap_ratio.toFixed(2)}X` : 'N/A'}
                        </TableCell>
                        <TableCell className="px-2 py-2 text-right text-[10px]">
                          {item.historical_win_ratio !== null ? `${item.historical_win_ratio.toFixed(1)}%` : 'N/A'}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={13} className="text-center py-2 text-[10px]">No data available</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default DataTable;
